import { forwardRef, memo, ReactNode, useCallback } from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import type { CKEditor } from '@ckeditor/ckeditor5-react';
import type { InlineEditor as CKInlineEditor } from '@ckeditor/ckeditor5-editor-inline';

import { cn } from '../../lib/styles';
import { Label } from '../../designSystem/Label';
import { InlineEditor, InlineEditorProps } from '@page/core/components/editor/InlineEditor';

const richTextInputVariants = cva('relative flex items-center w-full', {
  variants: {
    variant: {
      default:
        'bg-bg-inter-secondary-default rounded-sm outline-1 outline-bg-inter-secondary-default focus-within:outline-border-input-select transition-all',
      unstyled: 'border-none outline-none',
    },
    size: {
      sm: 'p-2 min-h-8',
      md: 'p-2 min-h-10',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'sm',
  },
});

export type RichTextInputProps = Omit<InlineEditorProps, 'sx' | 'data' | 'onDataChange'> &
  VariantProps<typeof richTextInputVariants> & {
    id?: string;
    value?: InlineEditorProps['data'];
    onChange?: InlineEditorProps['onDataChange'];
    label?: React.ReactNode;
    error?: React.ReactNode;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    containerClassName?: string;
    editorClassName?: string;
    leftIconClassName?: string;
    rightIconClassName?: string;
    className?: string;
    placeholder?: string;
    additionalContent?: ReactNode;
    onSubmit?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  };

const RichTextInputBase = (
  {
    id,
    value,
    onChange,
    className,
    label,
    error,
    leftIcon,
    rightIcon,
    containerClassName,
    editorClassName,
    leftIconClassName,
    rightIconClassName,
    size = 'sm',
    variant = 'default',
    toolbar,
    readonly,
    autofocus,
    onKeyDown,
    onSubmit,
    onReady,
    onFocus,
    onBlur,
    placeholder,
    additionalContent,
    ...rest
  }: RichTextInputProps,
  ref: React.ForwardedRef<CKEditor<CKInlineEditor>>,
) => {
  const actualId = id;

  const getContainerStyles = () => {
    return cn(richTextInputVariants({ variant, size }), error && 'outline-text-destructive prose', containerClassName);
  };

  const getEditorStyles = () => {
    return cn(
      'w-full font-body-md text-text-primary placeholder:text-text-tertiary',
      leftIcon && 'pl-6',
      rightIcon && 'pr-6',
      editorClassName,
    );
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        onSubmit?.(event);
      }

      onKeyDown?.(event);
    },
    [onKeyDown, onSubmit],
  );

  const editorElement = (
    <div className={getContainerStyles()} onKeyDown={handleKeyDown}>
      {leftIcon && <div className={cn('absolute left-3 flex items-center z-10', leftIconClassName)}>{leftIcon}</div>}

      <div className={cn('normalized-markdown font-body-md', getEditorStyles())}>
        <InlineEditor
          ref={ref}
          data={value}
          toolbar={toolbar}
          readonly={readonly}
          autofocus={autofocus}
          onDataChange={onChange}
          onReady={onReady}
          onFocus={onFocus}
          onBlur={onBlur}
          sx={{ fontSize: 13 }}
          {...rest}
        />
      </div>

      {rightIcon && (
        <div className={cn('absolute right-3 flex items-center z-10', rightIconClassName)}>{rightIcon}</div>
      )}

      {placeholder && !value && (
        <div
          data-slot="placeholder"
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-tertiary font-body-md"
        >
          {placeholder}
        </div>
      )}

      {additionalContent}
    </div>
  );

  if (!label) return editorElement;

  return (
    <div className={cn('flex flex-col', className)}>
      <Label htmlFor={actualId} className="mb-2">
        {label}
      </Label>

      {editorElement}

      <div className="h-5 pt-1 text-text-destructive font-body-sm">{error}</div>
    </div>
  );
};

export const RichTextInput = memo(forwardRef(RichTextInputBase));
